import type { FunctionComponent, ReactElement } from 'react';

import styled, { css } from 'styled-components';
import {
  Alert,
  boxShadow,
  P,
  paletteHelper,
  Radio,
  radiusHelper,
  spacingHelper,
  Strong,
  typographyHelper,
} from '@property.com.au/ui-kit';
import { PropertyRelationship } from '../PropertyRelationship';
import { FollowPropertyImage } from './subcomponents/FollowPropertyImage/FollowPropertyImage';
import { OutboundLink } from '../../../app-kit/components/OutboundLink/OutboundLink';
import type {
  AerialImage,
  ListingImage,
} from './subcomponents/FollowPropertyImage/FollowPropertyImage.types';

const OptionsContainer = styled.div`
  padding: ${spacingHelper('large')};
`;

const OptionsTitle = styled.div`
  ${typographyHelper('body01Bold')}
  margin-bottom: ${spacingHelper('medium')}
`;

const OptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacingHelper('extraSmall')};
`;

const Description = styled(P)`
  color: ${paletteHelper('textSecondary')};
`;

const StyledAlert = styled(Alert)`
  overflow: unset;
`;

// Using props to change color, due to inability to control label color via :checked attribute
// Due to legacy themes, inner circle can only be accessed like this
const StyledRadio = styled(Radio).attrs<{ $checked?: boolean }>((props) => ({
  dangerouslySetClassNames: {
    radioContainer: 'radio-container',
    label: 'radio-label',
  },
  $checked: props.$checked || false,
}))`
  ${({ $checked }) =>
    $checked &&
    css`
      .radio-label {
        ${typographyHelper('body01Bold')}
        color: ${paletteHelper('textBrand')};
      }

      .radio-container {
        > div:last-child {
          background-color: ${paletteHelper('backgroundBrandStatic')} !important;
        }
      }
    `}
`;

const InterestedDescription: FunctionComponent = () => {
  return (
    <Description>
      You can view all your <Strong>‘Just interested’</Strong> properties from your home dashboard.
      <br />
      <br />
      We have a lot of exciting features coming soon to keep you updated on the properties that
      you’re interested in.
    </Description>
  );
};

const MyHomeDescription: FunctionComponent = () => {
  return (
    <>
      <Description>
        Set your relationship to <Strong>‘My home’</Strong> and get quick access to your property on
        your home dashboard.
        <br />
        <br />
        We have a lot of exciting features coming soon to keep you updated on your home.
      </Description>
      <StyledAlert variant="info" sizeVariant="small" id="my_home_alert">
        Only one property can be set as My Home
      </StyledAlert>
    </>
  );
};

const InvestmentDescription: FunctionComponent = () => {
  return (
    <Description>
      You can view all your <Strong>‘Investment’</Strong> properties from your home dashboard.
      <br />
      <br />
      We have a lot of exciting features coming soon to keep you updated on your investment
      properties.
    </Description>
  );
};

const StyledOutboundLink = styled(OutboundLink)`
  ${typographyHelper('caption')}
  color: ${paletteHelper('textSecondary')};
  text-decoration: underline;

  margin-top: auto;

  &:hover {
    color: ${paletteHelper('textPrimary')};
  }
`;

const PropertyRelationshipDescriptionMapper: Record<PropertyRelationship, ReactElement> = {
  [PropertyRelationship.interested]: <InterestedDescription />,
  [PropertyRelationship.myHome]: <MyHomeDescription />,
  [PropertyRelationship.investment]: <InvestmentDescription />,
};

const ImageWrapper = styled.div`
  border-radius: ${radiusHelper('medium')};
  ${boxShadow.defaultBoxShadow}
`;

interface UpdateRelationshipModalContentProps {
  image?: ListingImage | AerialImage | null;
  propertyRelationship: PropertyRelationship;
  privacyLinkOutboundLinkType: string;
  onSelectionChange: (propertyRelationship: PropertyRelationship) => void;
}

export const UpdateRelationshipModalContent: FunctionComponent<
  UpdateRelationshipModalContentProps
> = ({ image, propertyRelationship, privacyLinkOutboundLinkType, onSelectionChange }) => {
  const privacyLink = {
    url: 'https://about.realestate.com.au/privacy/',
    displayText: 'Read our Privacy Policy',
    outboundLinkType: privacyLinkOutboundLinkType,
  };
  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSelectionChange(event.target.value as PropertyRelationship);
  };

  return (
    <>
      <ImageWrapper>
        <FollowPropertyImage image={image} />
        <OptionsContainer>
          <OptionsTitle>Your relationship to this property:</OptionsTitle>
          <OptionsWrapper onChange={onChange}>
            <StyledRadio
              name="Property relationship"
              id={PropertyRelationship.interested}
              value={PropertyRelationship.interested}
              defaultChecked={propertyRelationship === PropertyRelationship.interested}
              label="Just interested"
              $checked={propertyRelationship === PropertyRelationship.interested}
            />
            <StyledRadio
              name="Property relationship"
              id={PropertyRelationship.myHome}
              value={PropertyRelationship.myHome}
              defaultChecked={propertyRelationship === PropertyRelationship.myHome}
              label="My home"
              $checked={propertyRelationship === PropertyRelationship.myHome}
            />
            <StyledRadio
              name="Property relationship"
              id={PropertyRelationship.investment}
              value={PropertyRelationship.investment}
              defaultChecked={propertyRelationship === PropertyRelationship.investment}
              label="My investment property"
              $checked={propertyRelationship === PropertyRelationship.investment}
            />
          </OptionsWrapper>
        </OptionsContainer>
      </ImageWrapper>
      {PropertyRelationshipDescriptionMapper[propertyRelationship]}
      <StyledOutboundLink href={privacyLink.url} outboundLinkType={privacyLink.outboundLinkType}>
        {privacyLink.displayText}
      </StyledOutboundLink>
    </>
  );
};
